import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePopupForm } from "@/context/PopupFormContext";
import styles from "./PopupForm.module.scss";
import { getSiteConfig } from "@/config/siteConfigs";

const PopupForm: React.FC = () => {
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const { isOpenForm, closeForm } = usePopupForm();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setLoading(true);

    const { name, email, phone, message } = formData;

    const formPayload = {
      type: "feedback",
      legid: 1,
      language: siteConfig.language,
      data: {
        name: name,
        phone: phone,
        email: email,
        message: message,
      },
    };

    try {
      await sendToCRM(formPayload);

      alert(t("popupForm.success"));

      (window as any).dataLayer?.push({
        event: "form_submit",
        form_name: "popup_form",
        form_type: "feedback",
      });

      setFormData({ name: "", email: "", phone: "", message: "" });
      closeForm();
    } catch (error) {
      alert(t("popupForm.error"));
    } finally {
      setLoading(false);
    }
  };

  const sendToCRM = async (data: any) => {
    try {
      const res = await fetch("https://crm.hixa.eu/external/goodsapi/", {
        method: "POST",
        headers: {
          Authorization: `Bearer DH345DFGDGb32FDsf03876SDFSdf13`,
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: new URLSearchParams({
          type: data.type,
          legid: data.legid,
          language: siteConfig.language,
          ...Object.entries(data.data).reduce((acc, [key, value]) => {
            acc[key] = value as string;
            return acc;
          }, {} as Record<string, string>),
        }),
      });

      if (!res.ok) {
        throw new Error(`Failed to send message to CRM: ${res.statusText}`);
      }

      return await res.json();
    } catch (error) {
      throw error;
    }
  };

  return (
    <>
      {isOpenForm && (
        <div className={styles.popWrapper}>
          <div className="container">
            <div className={styles.popupForm} ref={popupRef}>
              <button
                className={styles.closePopup}
                onClick={closeForm}
                aria-label={t("common.close")}
              >
                <svg
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              <div className={styles.popupFormTitle}>
                <h3>{t("popupForm.title")}</h3>
              </div>

              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.formLine}>
                  <input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder={t("popupForm.fields.name")}
                  />
                </div>

                <div className={styles.formLine}>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder={t("popupForm.fields.phone")}
                  />
                </div>

                <div className={styles.formLine}>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t("popupForm.fields.email")}
                  />
                </div>

                <div className={styles.formLine}>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder={t("popupForm.fields.message")}
                  />
                </div>

                <button
                  type="submit"
                  className={`${styles.submitBtn} commonButton buttonLg`}
                  disabled={loading}
                >
                  {loading ? t("popupForm.submitting") : t("popupForm.submit")}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PopupForm;
